// CLUB-187: pure geometry helpers for rendering drawn lines & arrows, shared by
// the read-only map and the editor canvas. Operate on already-projected [x,y]
// screen points so they're independent of the map projection and unit-testable.

import type { GeoLineStyle, GeoPt } from './types'

export type Pt2 = [number, number]

// ─── App parity (mobile map_lines) ──────────────────────────────────────────

/** The app's shared picker palette — DomainColors.colorPickerValues verbatim
 * (natlink_ui_kit), rendered as a 4-column swatch grid like the app dialog. */
export const LINE_PICKER_COLORS = [
  '#649696', '#6496C8', '#C85555', '#FA6400',
  '#FA00C8', '#E91E63', '#9C27B0', '#3F51B5',
  '#2196F3', '#4CAF50', '#CDDC39', '#AAFFC3',
  '#FFE119', '#9A6324', '#EB914E', '#42555B',
] as const

/** App defaults for a freshly created line/arrow — kMapLineDefault{Color,Width,Style}. */
export const LINE_DEFAULT_COLOR = '#FFA500'
export const LINE_DEFAULT_WIDTH = 10
export const LINE_MIN_WIDTH = 4
export const LINE_MAX_WIDTH = 24

/** Packed ARGB int (the app's `color` field) → web hex. Alpha is discarded:
 * web has no opacity control, so preserving it would produce a hex string no
 * picker can represent. */
export function argbIntToHex(argb: number | undefined): string {
  if (argb === undefined || !Number.isFinite(argb)) return LINE_DEFAULT_COLOR
  const rgb = (argb >>> 0) & 0xffffff
  return `#${rgb.toString(16).padStart(6, '0').toUpperCase()}`
}

/** Web hex → packed ARGB int for the app's `color` field. Alpha is forced to
 * fully opaque, which is what every web-drawn line is. */
export function hexToArgbInt(hex: string | undefined): number {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex ?? '').trim())
  if (!m) return hexToArgbInt(LINE_DEFAULT_COLOR)
  return ((0xff << 24) | parseInt(m[1], 16)) >>> 0
}

/** A width guaranteed to be a finite number inside the app's picker range.
 * The picker can't produce anything else, but a persisted `lineWidth` can once
 * lines are read back (CLUB-528): an out-of-range value would blanket the map,
 * and a NaN reaching SVG `stroke-width` makes the whole polyline vanish with no
 * error anywhere. */
const clampLineWidth = (width: number | undefined): number => {
  if (width === undefined || Number.isNaN(width)) return LINE_DEFAULT_WIDTH
  return Math.min(LINE_MAX_WIDTH, Math.max(LINE_MIN_WIDTH, width))
}

/**
 * App width units → SVG user units (before the canvas scale factor).
 *
 * @deprecated Calibrated to web's own history, not to the app: it maps the app's
 * default width (10) onto "the 2-unit stroke the web editor has always drawn".
 * It is also expressed in SVG units, so the on-screen thickness scales with
 * window width — the same line measured 1.67 CSS px in an 800px window and 3.33
 * in a 1600px one. Use {@link lineStrokeWidthSvg}, which implements the app's
 * law in real pixels. Kept only for surfaces that have no map zoom to hand.
 */
export const lineStrokeWidth = (width: number | undefined): number =>
  (clampLineWidth(width) / LINE_DEFAULT_WIDTH) * 2

// ─── Rendered thickness (app parity) ────────────────────────────────────────
//
// The app's law, from the map engine (mapview_flutter `MapElementPaint`) and
// `map_line_zoom_scale.dart`:
//
//   interior_px = lineWidth × lineThicknessScale × 0.5 × √(scale / 0.168)
//   border_px   = 1.1 × interior_px
//
// `scale` is the live map's screen px per EPSG:3857 metre (`WidthScaler` is
// documented as `(zoom, scale)` with "scale (screen px per EPSG:3857 meter)"),
// and a paint's `width` is documented as logical pixels. EPSG:3857 metres are
// projected, so there is deliberately NO latitude correction here.
//
// Three things follow, and web previously got all three wrong:
//   • the width is a REAL pixel size, so it must not be left in SVG units —
//     otherwise it inflates with window width, since an SVG unit is
//     `max(cssW/520, cssH/560)` CSS px;
//   • the zoom response is the SQUARE ROOT of the map scale, not linear in
//     zoom (the app's comment notes it replaced exactly such a linear law);
//   • the dark border is a MULTIPLE of the interior, not a fixed pixel inset.
//     The app moved off a fixed 0.4 px border precisely because a fixed border
//     makes width-relative dash patterns drift with zoom.

/** EPSG:3857 world circumference in metres — the Web Mercator constant. */
export const PROJECTED_WORLD_METRES = 40075016.686
/** Tile layout size in CSS px; mirrors TILE_LAYOUT_PX and geo.ts's `256 * 2**zoom`. */
export const TILE_PX = 256
/** MapLibre's world uses 512-pixel tiles; the shared mobile law uses 256. */
export const MAPLIBRE_LAW_ZOOM_OFFSET = 1

/**
 * The app's `transform.scale`: screen (CSS) px per EPSG:3857 metre at `zoom`.
 *
 * Web's projection is standard Web Mercator over 256px tiles (`geo.ts` uses
 * `256 * 2 ** zoom` px for the whole world), so this is exact rather than
 * approximated — the two platforms are measuring the same quantity.
 */
export function mapPxPerProjectedMetre(zoom: number): number {
  return (TILE_PX * 2 ** zoom) / PROJECTED_WORLD_METRES
}

/** The scale at which a line draws at exactly its base width (app-side, tuned on device). */
const LINE_REFERENCE_SCALE = 0.168
/** trailWidthOuter (2.5) × tailThickness (2.0) ÷ defaultLineWidth (10). */
const LINE_WIDTH_FACTOR = 0.5
/** The dark border's width as a MULTIPLE of the interior width. */
export const LINE_BORDER_FACTOR = 1.1

/** Interior stroke in CSS px, per the app's law. */
export function lineStrokeWidthCssPx(
  width: number | undefined,
  zoom: number,
  thicknessScale = 1,
): number {
  const base = clampLineWidth(width) * thicknessScale * LINE_WIDTH_FACTOR
  return base * Math.sqrt(mapPxPerProjectedMetre(zoom) / LINE_REFERENCE_SCALE)
}

/**
 * Interior stroke in SVG user units — what the canvas actually needs.
 *
 * `mapScale` is CSS px per SVG unit (`max(cssW/520, cssH/560)`), so dividing by
 * it pins the stroke to a real pixel size the way every other on-screen
 * quantity on that canvas already is (label font `12 / mapScale`, arrowhead cap
 * `56 / mapScale`). Without it the line is the one element that grows with the
 * window.
 */
export function lineStrokeWidthSvg(
  width: number | undefined,
  opts: { zoom: number; mapScale: number; thicknessScale?: number },
): number {
  if (!(opts.mapScale > 0)) return 0
  return lineStrokeWidthCssPx(width, opts.zoom, opts.thicknessScale) / opts.mapScale
}

// ─── Thickness picker behaviour (app parity) ────────────────────────────────
//
// The app's picker is `SettingsSliderWidget` (ticks = 9) driving
// `SelectLineThicknessSheet` (4…24). Two laws govern it, and they are NOT the
// same law:
//
//   • Dragging or tapping the track is CONTINUOUS — the app maps the touch
//     fraction straight onto the range with no rounding, so real lines carry
//     fractional widths like 13.27.
//   • The −/+ steppers move by exactly ONE TICK of the track.
//
// The ticks are therefore the *stepper's* grid, not a snap grid for the drag,
// and they are the reason both laws have to be expressed against the same tick
// count. Web drew the 9 ticks but stepped by 1 and rounded the drag to whole
// numbers, which made the ticks decorative and put the app's own stepper stops
// (6.5, 11.5, 16.5, 21.5) out of reach entirely: 20 presses to cross the range
// instead of 8, and a fractional width authored on mobile silently rounded the
// moment the slider was touched on web.

/** Tick count on the thickness track — the app's `SettingsSliderWidget` default. */
export const LINE_WIDTH_TICKS = 9

/** One −/+ press: a single tick of the track (2.5 across the 4…24 range). */
export const LINE_WIDTH_STEP =
  (LINE_MAX_WIDTH - LINE_MIN_WIDTH) / (LINE_WIDTH_TICKS - 1)

/**
 * Move a width by `ticks` stepper presses (negative for −), clamped to range.
 *
 * Adds to wherever the value currently is rather than snapping to the tick
 * grid — matching the app, where stepping up from a dragged 13.27 gives 15.77,
 * not the nearest tick. Snapping would quietly discard a width the user set by
 * dragging, and would round every mobile-authored line on first press.
 */
export function stepLineWidth(width: number | undefined, ticks: number): number {
  return clampLineWidth(clampLineWidth(width) + ticks * LINE_WIDTH_STEP)
}

/** Track fraction (0…1, left to right) → width. Continuous, as in the app. */
export function lineWidthFromFraction(fraction: number): number {
  const f = Math.max(0, Math.min(1, Number.isNaN(fraction) ? 0 : fraction))
  return LINE_MIN_WIDTH + f * (LINE_MAX_WIDTH - LINE_MIN_WIDTH)
}

/** Width → 0…1 position on the track. Inverse of [lineWidthFromFraction]. */
export function lineWidthFraction(width: number | undefined): number {
  return (clampLineWidth(width) - LINE_MIN_WIDTH) / (LINE_MAX_WIDTH - LINE_MIN_WIDTH)
}

/**
 * Width for display beside the slider. Continuous drag means the raw value is
 * usually fractional, and rendering it unformatted printed all 17 digits of a
 * float; the app shows no number at all, so any short form is parity-neutral.
 */
export function formatLineWidth(width: number | undefined): string {
  return (Math.round(clampLineWidth(width) * 10) / 10).toString()
}

/** Dash pattern per app MapLineStyle, scaled to a stroke width so thick dashed
 * lines don't read as solid. Returns undefined for solid.
 *
 * The multipliers are the app's, expressed against the stroke they are drawn
 * on. MapLineWidget defines them against the *border* width — dashed [6,3],
 * dotted [1,2], dash-dot [6,2,1,2] — and nestedDashPatterns then insets each
 * run for the narrower interior stroke. For a default line (interior 10,
 * border 10.4) dashed resolves to a 62.0 dash and a 31.4 gap over a width of
 * 10, i.e. ~6.2 and ~3.1 — so the border-relative numbers carry over directly.
 *
 * Keeping the dot at a full stroke width matters: these render with
 * `stroke-linecap="butt"`, so a run much shorter than the stroke is a barely
 * visible sliver rather than a dot. */
export function lineDashArray(style: GeoLineStyle | undefined, strokeW: number): string | undefined {
  const runs = (...multiples: number[]) =>
    multiples.map((m) => Math.round(m * strokeW * 100) / 100).join(' ')
  switch (style) {
    case 'dashed':
      return runs(6, 3)
    case 'dotted':
      return runs(1, 2)
    case 'dash_dot':
      return runs(6, 2, 1, 2)
    default:
      return undefined
  }
}

/** Total geodesic (haversine) length of the polyline in meters — the web twin
 * of the app entity's lengthMeters. */
export function lineLengthMeters(points: GeoPt[]): number {
  if (points.length < 2) return 0
  const R = 6371000
  let total = 0
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]
    const b = points[i]
    const dLat = ((b.lat - a.lat) * Math.PI) / 180
    const dLon = ((b.lon - a.lon) * Math.PI) / 180
    const la = (a.lat * Math.PI) / 180
    const lb = (b.lat * Math.PI) / 180
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(la) * Math.cos(lb) * Math.sin(dLon / 2) ** 2
    total += 2 * R * Math.asin(Math.sqrt(h))
  }
  return total
}

/** "700 m" under a kilometre, "1.2 km" above — matches the app's length chip. */
export function formatLineLength(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`
  return `${(meters / 1000).toFixed(1)} km`
}

/** Point halfway along the polyline's screen-space length — the label anchor
 * (the web stand-in for the app's MapLineLabelLocationCalculator). */
export function polylineMidpoint(pts: Pt2[]): Pt2 | null {
  if (pts.length === 0) return null
  if (pts.length === 1) return pts[0]
  const segLens: number[] = []
  let total = 0
  for (let i = 1; i < pts.length; i++) {
    const len = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1])
    segLens.push(len)
    total += len
  }
  if (total === 0) return pts[0]
  let remaining = total / 2
  for (let i = 0; i < segLens.length; i++) {
    if (remaining <= segLens[i]) {
      const f = segLens[i] === 0 ? 0 : remaining / segLens[i]
      return [
        pts[i][0] + (pts[i + 1][0] - pts[i][0]) * f,
        pts[i][1] + (pts[i + 1][1] - pts[i][1]) * f,
      ]
    }
    remaining -= segLens[i]
  }
  return pts[pts.length - 1]
}

/** SVG `points` attribute string for a polyline through the given screen points. */
export function polylinePoints(pts: Pt2[]): string {
  return pts.map(([x, y]) => `${x},${y}`).join(' ')
}

/** The app's black outline around lines & arrowheads: 0.2 logical px per
 * side (MapLineWidget _borderWidth). Add to a stroke width for the under-
 * stroke, or use as-is for the head's outline stroke width. */
export const LINE_OUTLINE_WIDTH = 0.4

/**
 * SVG path for the app's filled triangular arrowhead at the last point,
 * pointing along the final segment. Geometry mirrors MapLineCapCalculator:
 * a 22.5° opening angle, the tip extending ⅔ of the head height past the
 * endpoint with the base ⅓ behind it. The base width is 5× the line's stroke
 * width (MapLineCapWidget _arrowHeadSizeFactor), clamped to 40% of the path
 * length and an optional absolute ceiling (the app's 56 px screen cap) so a
 * short line's head can't dwarf it (WET-4184). Returns null when there aren't
 * two distinct points to derive a direction from.
 */
export function arrowHeadPath(pts: Pt2[], strokeW: number, maxSize = Infinity): string | null {
  if (pts.length < 2) return null
  const [tx, ty] = pts[pts.length - 1]
  // Walk back to the first point that differs, so a zero-length final segment
  // (duplicate point) still yields a sensible direction.
  let fx = tx
  let fy = ty
  for (let i = pts.length - 2; i >= 0; i--) {
    if (pts[i][0] !== tx || pts[i][1] !== ty) {
      fx = pts[i][0]
      fy = pts[i][1]
      break
    }
  }
  const dx = tx - fx
  const dy = ty - fy
  const len = Math.hypot(dx, dy)
  if (len === 0) return null
  let pathLen = 0
  for (let i = 1; i < pts.length; i++) {
    pathLen += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1])
  }
  const size = Math.min(strokeW * 5, pathLen * 0.4, maxSize)
  if (size <= 0) return null
  const ux = dx / len
  const uy = dy / len
  // Head height from the base width and the 22.5° opening angle.
  const h = size / 2 / Math.tan(Math.PI / 8)
  const tipX = tx + ux * h * (2 / 3)
  const tipY = ty + uy * h * (2 / 3)
  const bcx = tx - (ux * h) / 3
  const bcy = ty - (uy * h) / 3
  const phx = (uy * size) / 2
  const phy = (-ux * size) / 2
  const r = (n: number) => Math.round(n * 100) / 100
  return `M ${r(tipX)} ${r(tipY)} L ${r(bcx + phx)} ${r(bcy + phy)} L ${r(bcx - phx)} ${r(bcy - phy)} Z`
}
